/* FleekPure · shared behaviour for fleek-* sections (4–13)
 *   <fleek-carousel>  horizontal rail with arrows that hide at the ends
 *   <fleek-switcher>  tabs or slides switched one at a time (tabs, arrows, dots, swipe, autoplay)
 *   <fleek-compare>   before / after drag slider
 *   <fleek-gallery>   opens images from a list in a full-screen viewer
 */

const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/* ───────── Carousel ───────── */
class FleekCarousel extends HTMLElement {
  connectedCallback() {
    if (this._ready) return;
    this._ready = true;
    this.rail = this.querySelector('[data-rail]');
    this.prev = this.querySelector('[data-dir="-1"]');
    this.next = this.querySelector('[data-dir="1"]');
    if (!this.rail) return;

    const update = () => {
      const max = this.rail.scrollWidth - this.rail.clientWidth - 2;
      const scrollable = max > 0;
      [this.prev, this.next].forEach((b) => b && (b.hidden = !scrollable));
      if (this.prev) this.prev.disabled = this.rail.scrollLeft <= 2;
      if (this.next) this.next.disabled = this.rail.scrollLeft >= max;
    };
    [this.prev, this.next].forEach((btn) =>
      btn?.addEventListener('click', () =>
        this.rail.scrollBy({ left: Number(btn.dataset.dir) * this.rail.clientWidth * 0.8 })
      )
    );
    this.rail.addEventListener('scroll', () => requestAnimationFrame(update), { passive: true });
    this._ro = new ResizeObserver(update);
    this._ro.observe(this.rail);
    update();
  }
  disconnectedCallback() {
    this._ro?.disconnect();
  }
}

/* ───────── Switcher (tabs / slider) ─────────
 * data-mode="slider" slides a [data-track]; data-mode="tabs" shows one [data-panel] at a time.
 * [data-tab="i"] buttons, [data-step="±1"] arrows, [data-dot="i"] dots, data-autoplay="seconds".
 */
class FleekSwitcher extends HTMLElement {
  connectedCallback() {
    if (this._ready) return;
    this._ready = true;
    this.mode = this.dataset.mode || 'tabs';
    this.track = [...this.querySelectorAll('[data-track]')].find((t) => t.closest('fleek-switcher') === this) || null;
    this.panels = [...this.querySelectorAll('[data-panel]')].filter((p) => p.closest('fleek-switcher') === this);
    this.tabs = [...this.querySelectorAll('[data-tab]')].filter((t) => t.closest('fleek-switcher') === this);
    this.dots = [...this.querySelectorAll('[data-dot]')].filter((d) => d.closest('fleek-switcher') === this);
    this.steps = [...this.querySelectorAll('[data-step]')].filter((s) => s.closest('fleek-switcher') === this);
    this.count = this.panels.length;
    if (this.count === 0) return;

    this.index = Math.min(Number(this.dataset.start) || 0, this.count - 1);

    this.tabs.forEach((tab) => {
      tab.addEventListener('click', () => this.go(Number(tab.dataset.tab), true));
      tab.addEventListener('keydown', (e) => {
        if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
        e.preventDefault();
        this.go(this.index + (e.key === 'ArrowRight' ? 1 : -1), true);
        this.tabs.find((t) => Number(t.dataset.tab) === this.index)?.focus();
      });
    });
    this.dots.forEach((dot) => dot.addEventListener('click', () => this.go(Number(dot.dataset.dot), true)));
    this.steps.forEach((btn) => btn.addEventListener('click', () => this.go(this.index + Number(btn.dataset.step), true)));
    if (this.count < 2) this.steps.forEach((b) => (b.hidden = true));

    if ((this.mode === 'slider' || this.mode === 'fade') && this.track) this.#initSwipe();

    const seconds = Number(this.dataset.autoplay) || 0;
    if (seconds > 0 && this.count > 1 && !reduceMotion()) {
      this._autoplay = seconds * 1000;
      this.style.setProperty('--fk-autoplay', `${seconds}s`);
      this.#startAutoplay();
      const pause = () => { this._paused = true; this.#stopAutoplay(); };
      const resume = () => { this._paused = false; this.#startAutoplay(); };
      this.addEventListener('mouseenter', pause);
      this.addEventListener('mouseleave', resume);
      this.addEventListener('focusin', pause);
      this.addEventListener('focusout', (e) => { if (!this.contains(e.relatedTarget) && !this.matches(':hover')) resume(); });
      document.addEventListener('visibilitychange', () => (document.hidden ? this.#stopAutoplay() : this.#startAutoplay()));
    }

    this.go(this.index, false);
  }

  disconnectedCallback() {
    this.#stopAutoplay();
  }

  go(i, user = false) {
    this.index = ((i % this.count) + this.count) % this.count;

    if (this.mode === 'slider' && this.track) {
      this.track.style.transform = `translateX(${-100 * this.index}%)`;
      this.panels.forEach((p, k) => {
        p.setAttribute('aria-hidden', String(k !== this.index));
        p.inert = k !== this.index;
      });
    } else if (this.mode === 'fade') {
      this.panels.forEach((p, k) => {
        p.setAttribute('aria-hidden', String(k !== this.index));
        p.inert = k !== this.index;
      });
    } else {
      this.panels.forEach((p, k) => (p.hidden = k !== this.index));
    }
    this.panels.forEach((p, k) => p.classList.toggle('is-active', k === this.index));

    this.tabs.forEach((t) => {
      const on = Number(t.dataset.tab) === this.index;
      t.setAttribute('aria-selected', String(on));
      t.tabIndex = on ? 0 : -1;
    });
    this.dots.forEach((d) => d.setAttribute('aria-current', String(Number(d.dataset.dot) === this.index)));
    this.#syncProgress();

    if (user && this._autoplay) {
      this.#stopAutoplay();
      this.#startAutoplay();
    }
  }

  #initSwipe() {
    let x0 = null;
    let y0 = null;
    this.track.addEventListener('touchstart', (e) => {
      x0 = e.touches[0].clientX;
      y0 = e.touches[0].clientY;
    }, { passive: true });
    this.track.addEventListener('touchend', (e) => {
      if (x0 === null) return;
      const dx = e.changedTouches[0].clientX - x0;
      const dy = e.changedTouches[0].clientY - y0;
      if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) this.go(this.index + (dx < 0 ? 1 : -1), true);
      x0 = null;
    });
  }

  #startAutoplay() {
    if (!this._autoplay || this._timer || this._paused) return;
    this._timer = setInterval(() => this.go(this.index + 1), this._autoplay);
    this.#syncProgress();
  }
  #stopAutoplay() {
    clearInterval(this._timer);
    this._timer = null;
    this.#syncProgress();
  }
  /* Restarts the progress fill on the current dot while autoplay runs */
  #syncProgress() {
    this.dots.forEach((d) => d.classList.remove('is-run'));
    if (!this._timer) return;
    const current = this.dots.find((d) => Number(d.dataset.dot) === this.index);
    if (!current) return;
    void current.offsetWidth;
    current.classList.add('is-run');
  }
}

/* ───────── Before / after compare ───────── */
class FleekCompare extends HTMLElement {
  connectedCallback() {
    if (this._ready) return;
    this._ready = true;
    this.input = this.querySelector('input[type="range"]');
    if (!this.input) return;
    const set = () => this.style.setProperty('--fk-pos', `${this.input.value}%`);
    this.input.addEventListener('input', set);
    set();
  }
}

/* ───────── Image gallery viewer ───────── */
class FleekGallery extends HTMLElement {
  connectedCallback() {
    if (this._ready) return;
    this._ready = true;
    this.items = [...this.querySelectorAll('[data-zoom-src]')];
    this.dialog = this.querySelector('[data-lightbox]');
    this.img = this.querySelector('[data-lightbox-img]');
    if (!this.items.length || !this.dialog || !this.img) return;
    this.current = 0;

    this.items.forEach((item, i) => item.addEventListener('click', () => this.open(i)));
    this.dialog.querySelector('[data-close]')?.addEventListener('click', () => this.dialog.close());
    this.dialog.querySelectorAll('[data-lb-step]').forEach((b) => {
      b.hidden = this.items.length < 2;
      b.addEventListener('click', () => this.open(this.current + Number(b.dataset.lbStep)));
    });
    this.dialog.addEventListener('click', (e) => {
      if (e.target === this.dialog || e.target.classList.contains('fk-lightbox__inner')) this.dialog.close();
    });
    this.dialog.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') this.open(this.current - 1);
      if (e.key === 'ArrowRight') this.open(this.current + 1);
    });
    this.dialog.addEventListener('close', () => {
      document.documentElement.style.overflow = '';
      this.items[this.current]?.focus({ preventScroll: true });
    });
  }

  open(i) {
    const n = this.items.length;
    this.current = ((i % n) + n) % n;
    const item = this.items[this.current];
    this.img.src = item.dataset.zoomSrc;
    this.img.alt = item.dataset.zoomAlt || '';
    if (!this.dialog.open) {
      this.dialog.showModal();
      document.documentElement.style.overflow = 'hidden';
    }
  }
}

/* ───────── Card add to cart ─────────
 * Any <form data-fleek-atc> posts to the cart with ajax and tells Horizon's cart drawer,
 * the same way the theme's own product form does. Falls back to a normal form post. */
const cartRoutes = () => (window.Theme && window.Theme.routes) || {};

async function fleekAddToCart(form) {
  const button = form.querySelector('[type="submit"]');
  const error = form.querySelector('[role="alert"]');
  if (error) error.hidden = true;
  button?.setAttribute('aria-busy', 'true');

  const formData = new FormData(form);
  const sectionIds = [...document.querySelectorAll('cart-items-component')]
    .map((el) => el.dataset.sectionId)
    .filter(Boolean);
  if (sectionIds.length) formData.append('sections', sectionIds.join(','));
  const quantity = Number(formData.get('quantity')) || 1;

  let events = null;
  try { events = await import('@shopify/events'); } catch (_) { events = null; }
  let deferred = null;
  if (events?.CartLinesUpdateEvent) {
    deferred = events.CartLinesUpdateEvent.createPromise();
    form.dispatchEvent(
      new events.CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: String(formData.get('id')), quantity }],
        promise: deferred.promise,
      })
    );
  }

  try {
    const res = await fetch(cartRoutes().cart_add_url || '/cart/add.js', {
      method: 'POST',
      headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
      body: formData,
    });
    const data = await res.json();
    if (data.status) throw new Error(data.description || data.message || 'Could not add to cart');

    if (deferred && events) {
      const cart = await (await fetch(`${cartRoutes().cart_url || '/cart'}.js`, { headers: { Accept: 'application/json' } })).json();
      deferred.resolve({
        cart: events.CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { items: cart.items, source: 'fleek-card', itemCount: quantity, sections: data.sections, didError: false },
      });
    } else {
      window.location.href = cartRoutes().cart_url || '/cart';
    }
  } catch (err) {
    deferred?.reject(err);
    if (error) {
      error.textContent = err.message;
      error.hidden = false;
    }
  } finally {
    button?.removeAttribute('aria-busy');
  }
}

if (!window.__fleekAtcBound) {
  window.__fleekAtcBound = true;
  document.addEventListener('submit', (e) => {
    const form = e.target;
    if (!(form instanceof HTMLFormElement) || !form.hasAttribute('data-fleek-atc')) return;
    e.preventDefault();
    fleekAddToCart(form);
  });
}

if (!customElements.get('fleek-carousel')) customElements.define('fleek-carousel', FleekCarousel);
if (!customElements.get('fleek-switcher')) customElements.define('fleek-switcher', FleekSwitcher);
if (!customElements.get('fleek-compare')) customElements.define('fleek-compare', FleekCompare);
if (!customElements.get('fleek-gallery')) customElements.define('fleek-gallery', FleekGallery);
